import { Link, useLocation, useNavigate } from "react-router-dom";
import "./VerifyEmail.css";
import { useRef, useState, type KeyboardEvent, type FormEvent, type ClipboardEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import LogoMark from "../images/LogoMark";
import Button from "../ui/Button";
import { verifyEmail, sendVerificationEmail } from "../../api/Client";
import Modal from "../ui/Modal";

const CODE_LENGTH = 6;

export default function VerifyEmail() {
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Passed from AuthPage on signup — if it's missing (e.g. a direct link,
    // or a page refresh, which drops router state), fall back to a generic
    // message rather than showing a broken "undefined".
    const email = (location.state as { email?: string } | null)?.email;

    const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(""));
    const [resendOpen, setResendOpen] = useState(false);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const code = digits.join("");

    const verifyMutation = useMutation({
        mutationFn: () => verifyEmail(code),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["me"] });
            navigate("/learn", { replace: true });
        },
        onError: () => {
            // Wrong/expired code — clear the boxes so the user isn't left
            // staring at digits that already failed, and refocus the first one.
            setDigits(Array(CODE_LENGTH).fill(""));
            inputRefs.current[0]?.focus();
        },
    });

    const resendMutation = useMutation({
        mutationFn: () => sendVerificationEmail(),
        onSuccess: () => setResendOpen(true),
    });

    const setDigit = (index: number, value: string) => {
        // Only keep the last character typed, and only if it's a digit —
        // covers both normal typing and pasting a single stray character.
        const clean = value.replace(/\D/g, "").slice(-1);

        setDigits((prev) => {
            const next = [...prev];
            next[index] = clean;
            return next;
        });

        if (clean && index < CODE_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace" && !digits[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
        if (!pasted) return;

        e.preventDefault();
        setDigits((prev) => {
            const next = [...prev];
            for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
            return next;
        });
        inputRefs.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (code.length === CODE_LENGTH) {
            verifyMutation.mutate();
        }
    };

    return (
        <>
            <Modal open={resendOpen}
                onClose={() => setResendOpen(false)}
                title="Email Sent."
                actionLabel="OK">
                Check your inbox for the new code.
            </Modal>
            <div className="auth-page">
                <header className="auth-page__header">
                    <Link to="/" className="auth-page__brand">
                        <LogoMark size={28}/>
                        <span>Eolass</span>
                    </Link>
                </header>

                <main className="auth-page__main">
                    <div className="auth-card">
                        <div className="auth-card__body">
                            <span className="eyebrow auth-card__eyebrow">One more step</span>
                            <h1 className="auth-card__title">Verify your email</h1>
                            <p className="verify-email__copy">
                                We sent a 6-digit code to {email ? <strong>{email}</strong> : "your email"}.
                                Enter it below to activate your account.
                            </p>

                            <form className="auth-form" noValidate onSubmit={handleSubmit}>
                                <div className="verify-code">
                                    {digits.map((digit, i) => (
                                        <input
                                            key={i}
                                            ref={(el) => { inputRefs.current[i] = el; }}
                                            className="verify-code__digit"
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={1}
                                            aria-label={`Digit ${i + 1}`}
                                            value={digit}
                                            onChange={(e) => setDigit(i, e.target.value)}
                                            onKeyDown={handleKeyDown(i)}
                                            onPaste={handlePaste}
                                        />
                                    ))}
                                </div>

                                {verifyMutation.isError && (
                                    <p className="auth-form__error" role="alert">
                                        {(verifyMutation.error as Error).message || "That code didn't work. Try again."}
                                    </p>
                                )}

                                <Button
                                    type="submit"
                                    variant="primary"
                                    size="lg"
                                    className="auth-form__submit"
                                    disabled={code.length !== CODE_LENGTH || verifyMutation.isPending}
                                >
                                    {verifyMutation.isPending ? "Verifying…" : "Verify email"}
                                </Button>
                            </form>

                            <p className="auth-card__switch">
                                Didn't get a code?{" "}
                                <button type="button" onClick={() => resendMutation.mutate()} disabled={resendMutation.isPending}>
                                    {resendMutation.isPending ? "Sending…" : "Resend email"}
                                </button>
                            </p>
                            <p className="auth-card__switch">
                                Wrong email address? <Link to="/signup">Change it</Link>
                            </p>
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
}
